/**
 * CryptoTicker Component
 *
 * Horizontal scrolling ticker bar displaying live cryptocurrency prices
 * fetched from Bybit V5 public API via the backend proxy.
 *
 * Features:
 * - Auto-refreshes every 30 seconds
 * - Seamless infinite scroll animation
 * - Color-coded 24h change indicators
 * - Loading skeleton and error fallback
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { logger } from "../../services/logger";
import "./crypto-ticker.css";

interface TickerEntry {
  symbol: string;
  name: string;
  price: string;
  change24h_pct: string;
  high24h: string;
  low24h: string;
}

const REFRESH_INTERVAL_MS = 30000;
const FETCH_TIMEOUT_MS = 8000;

interface ChangeDisplay {
  value: string;
  isPositive: boolean;
}

export function CryptoTicker(): JSX.Element {
  const [tickers, setTickers] = useState<TickerEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const mountedRef = useRef(true);

  const fetchTickers = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch("/api/market/tickers", {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });

      clearTimeout(timeoutId);

      if (!mountedRef.current) {
        return;
      }

      if (!response.ok) {
        throw new Error("HTTP " + String(response.status) + ": " + response.statusText);
      }

      const data: TickerEntry[] = await response.json();

      if (!mountedRef.current) {
        return;
      }

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format: expected array");
      }

      setTickers(data);
      setHasError(false);
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }

      if (err instanceof DOMException && err.name === "AbortError") {
        logger.warn("CryptoTicker fetch aborted (timeout or unmount)");
      } else if (err instanceof Error) {
        logger.error("CryptoTicker fetch failed", err);
      } else {
        logger.error("CryptoTicker fetch failed with unknown error");
      }

      setHasError(true);
    } finally {
      clearTimeout(timeoutId);
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchTickers();
    const intervalId = setInterval(fetchTickers, REFRESH_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [fetchTickers]);

  const formatChangeDisplay = useCallback(
    (pct: string): ChangeDisplay => {
      const num = parseFloat(pct);
      if (isNaN(num)) {
        return { value: pct, isPositive: true };
      }
      const isPositive = num >= 0;
      const prefix = isPositive ? "+" : "";
      return { value: prefix + num.toFixed(2) + "%", isPositive };
    },
    [],
  );

  function renderSkeletonItems(): JSX.Element[] {
    const items: JSX.Element[] = [];
    for (let i = 0; i < 6; i++) {
      items.push(
        <div key={i} className="crypto-ticker__skeleton-item">
          <div className="crypto-ticker__skeleton-symbol" />
          <div className="crypto-ticker__skeleton-price" />
          <div className="crypto-ticker__skeleton-change" />
        </div>,
      );
    }
    return items;
  }

  if (isLoading) {
    return (
      <div
        className="crypto-ticker"
        role="status"
        aria-label="Loading cryptocurrency prices"
      >
        <div className="crypto-ticker__track">
          <div className="crypto-ticker__skeleton">
            {renderSkeletonItems()}
          </div>
          <div className="crypto-ticker__skeleton">
            {renderSkeletonItems()}
          </div>
        </div>
      </div>
    );
  }

  if (tickers.length === 0) {
    return (
      <div
        className="crypto-ticker crypto-ticker--error"
        role="status"
        aria-label="Cryptocurrency prices temporarily unavailable"
      >
        <div className="crypto-ticker__track">
          <span className="crypto-ticker__item">
            <span className="crypto-ticker__symbol">
              Market data temporarily unavailable
            </span>
          </span>
        </div>
      </div>
    );
  }

  const renderedItems = tickers.map((entry) => {
    const display = formatChangeDisplay(entry.change24h_pct);
    const changeClass =
      "crypto-ticker__change " +
      (display.isPositive
        ? "crypto-ticker__change--positive"
        : "crypto-ticker__change--negative");

    return (
      <React.Fragment key={entry.symbol}>
        <span className="crypto-ticker__separator" aria-hidden="true">
          |
        </span>
        <span className="crypto-ticker__item">
          <span className="crypto-ticker__symbol">{entry.name}</span>
          <span className="crypto-ticker__price">${entry.price}</span>
          <span className={changeClass}>{display.value}</span>
        </span>
      </React.Fragment>
    );
  });

  const tickerClass =
    "crypto-ticker" + (hasError ? " crypto-ticker--error" : "");

  return (
    <div
      className={tickerClass}
      role="marquee"
      aria-label="Live cryptocurrency prices"
      aria-live="polite"
    >
      <div className="crypto-ticker__track">
        {renderedItems}
        {renderedItems}
      </div>
    </div>
  );
}

export default CryptoTicker;
